import { mm } from "@inovitas/infra3dsdk";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import ClientConfig from "shared/config";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import {
  downloadFile,
  getApiKeyHeaders,
  getFileExtensionFromURL,
} from "shared/common/utils/general-utils";
import {
  getFeatureCollectionFromLayer,
  getFeaturesCountFromLayer,
} from "shared/common/utils/layers/layerutils";
import { useTranslation } from "react-i18next";

interface LayerDownloadDialogProps {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  setLayerMenuID: (id: string) => void;
  layer: mm.Layer;
}

export default function LayerDownloadDialog({
  dialogOpen,
  setDialogOpen,
  setLayerMenuID,
  layer,
}: LayerDownloadDialogProps) {
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const apm = defaultStore((state) => state.apm);

  const [exportFormat, setExportFormat] = useState("");
  const [featuresCount, setFeaturesCount] = useState(-1);
  const [downloading, setDownloading] = useState(false);

  const { t } = useTranslation();

  const maxFeaturesForExport = 10000;

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const downloadLayer = async (layer: mm.Layer) => {
    setDownloading(true);
    type FeatureCollectionWithCRS = GeoJSON.FeatureCollection & {
      crs?: {
        type: string;
        properties: {
          name: string;
        };
      };
    };
    const feature_collection: FeatureCollectionWithCRS =
      await getFeatureCollectionFromLayer(layer);
    if (!feature_collection.crs) {
      feature_collection.crs = {
        type: "name",
        properties: {
          name: "EPSG:" + layer.scene.getCurrentGeoframe().srid,
        },
      };
    }
    const formData = new FormData();
    formData.append("format", exportFormat);
    formData.append("inputdata", JSON.stringify(feature_collection));
    // get url for converted layer features
    const res = await fetch(`${ClientConfig.conversionURL}/convert`, {
      method: "POST",
      headers: new Headers({
        ...getApiKeyHeaders(),
      }),
      body: formData,
    });
    setDownloading(false);
    if (!res.ok) {
      changeAlertMessage({
        message: t("alert_error.fetching_download_url_failed"),
        type: "error",
      });
      return;
    }
    const { data } = await res.json();
    if (data) {
      // download layer export file
      downloadFile(
        `${ClientConfig.conversionURL}${data}`,
        `${layer.layername}.${getFileExtensionFromURL(data)}`,
        new Headers({
          ...getApiKeyHeaders(),
        })
      )
        .then((res) => {
          if (res) {
            setDialogOpen(false);
            setLayerMenuID("");
            changeAlertMessage({
              message: t("alert_success.layer_downloaded"),
              type: "success",
            });
            monitor(apm, "download-layer | Layer Download", undefined, {
              format: exportFormat,
            });
          }
        })
        .catch((err) => {
          changeAlertMessage({
            message: t("alert_error.download_failed"),
            type: "error",
          });
        });
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      getFeaturesCountFromLayer(layer as mm.Layer).then((count) => {
        setFeaturesCount(count);
      });
    } else {
      setFeaturesCount(-1);
    }
  }, [dialogOpen]);

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ color: "darkblue.main" }}>
        <Typography fontSize={"18px"} fontWeight={700}>
          {t("edit_layers.download_layer")}: {layer.layername}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          sx={{ color: "darkblue.main" }}
        >
          <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
            {featuresCount === -1 ? (
              <Skeleton variant="text" />
            ) : (
              <Typography
                fontSize={"14px"}
                fontWeight={400}
                color={featuresCount > maxFeaturesForExport ? "red" : ""}
              >
                {featuresCount} {t("edit_layers.maximum_objects")}
              </Typography>
            )}

            <div style={{ display: "flex", gap: "8px" }}>
              <FormControl
                size="small"
                sx={{
                  minWidth: "200px",
                }}
              >
                <InputLabel>{t("general.format")}</InputLabel>
                <Select
                  label={t("general.format")}
                  onChange={(e) => setExportFormat(e.target.value)}
                  value={exportFormat}
                  disabled={
                    featuresCount === -1 || featuresCount > maxFeaturesForExport
                  }
                >
                  <MenuItem value="GeoJSON">GeoJSON</MenuItem>
                  <MenuItem value="KML">KML</MenuItem>
                  <MenuItem value="GML">GML</MenuItem>
                  <MenuItem value="ESRI Shapefile">ESRI Shapefile</MenuItem>
                  <MenuItem value="DXF">DXF</MenuItem>
                </Select>
              </FormControl>
              <LoadingButton
                loading={downloading}
                disabled={
                  !exportFormat ||
                  featuresCount === -1 ||
                  featuresCount > maxFeaturesForExport
                }
                variant="outlined"
                onClick={() => downloadLayer(layer as mm.Layer)}
              >
                {t("general.download")}
              </LoadingButton>
            </div>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>{t("general.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
